import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, EnvelopeSimple } from "@phosphor-icons/react";

/** Six-digit email code step. `mfa` comes from the login / Google / invitation response. */
export function TwoFactor({ mfa, onSuccess }) {
  const [state, setState] = useState(mfa);
  const [code, setCode] = useState("");
  const [recovery, setRecovery] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [wait, setWait] = useState(mfa.resend_in || 60);

  useEffect(() => {
    if (wait <= 0) return undefined;
    const t = setTimeout(() => setWait((w) => w - 1), 1000);
    return () => clearTimeout(t);
  }, [wait]);

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/2fa/verify", { mfa_token: state.mfa_token, code });
      onSuccess(data);
    } catch (err) {
      setError(apiError(err.response?.data?.detail));
      setCode("");
    }
    setBusy(false);
  };

  const resend = async () => {
    setError("");
    try {
      const { data } = await api.post("/auth/2fa/resend", { mfa_token: state.mfa_token });
      setState({ ...state, ...data });
      setWait(data.resend_in || 60);
    } catch (err) {
      setError(apiError(err.response?.data?.detail));
    }
  };

  const undeliverable = state.delivery && state.delivery !== "sent";

  return (
    <form onSubmit={verify} className="mt-8 space-y-5" data-testid="twofa-form">
      <div className="flex items-start gap-4 bg-blue-50 rounded-2xl p-4">
        <ShieldCheck size={28} weight="duotone" className="text-[#3B82F6] shrink-0" />
        <p className="text-sm">
          We sent a 6-digit code to <strong data-testid="twofa-email-hint">{state.email_hint}</strong>.
          It expires in {state.expires_in_minutes || 10} minutes.
        </p>
      </div>
      {undeliverable && (
        <p className="text-sm font-semibold text-amber-700 bg-amber-50 rounded-2xl p-4" data-testid="twofa-delivery-warning">
          The code could not be delivered to this address ({state.delivery.replace(/_/g, " ")}). Ask a super administrator
          for a one-time recovery code and enter it below.
        </p>
      )}
      <div>
        <Label className="text-sm font-bold">{recovery ? "Recovery code" : "Verification code"}</Label>
        <Input autoFocus inputMode={recovery ? "text" : "numeric"} autoComplete="one-time-code" required
               value={code} onChange={(e) => setCode(recovery ? e.target.value.toUpperCase() : e.target.value.replace(/\D/g, "").slice(0, 6))}
               className="mt-2 h-14 rounded-xl text-center text-2xl font-bold tracking-[0.4em]"
               placeholder={recovery ? "XXXXXXXX" : "••••••"} data-testid="twofa-code" />
      </div>
      {error && <p className="text-sm font-semibold text-[#EF4444]" data-testid="twofa-error">{error}</p>}
      <Button type="submit" disabled={busy || code.length < 6}
              className="w-full h-12 rounded-full bg-[#3B82F6] hover:bg-[#2563EB] font-bold active:scale-95"
              style={{ transition: "transform .2s ease, background-color .2s ease" }} data-testid="twofa-submit">
        {busy ? "Checking…" : "Verify and continue"}
      </Button>
      <div className="flex flex-wrap justify-between gap-3 text-sm">
        <button type="button" onClick={resend} disabled={wait > 0 || recovery}
                className="font-bold text-[#3B82F6] disabled:text-muted-foreground inline-flex items-center gap-1"
                data-testid="twofa-resend">
          <EnvelopeSimple size={16} /> {wait > 0 ? `Send a new code in ${wait}s` : "Send a new code"}
        </button>
        <button type="button" onClick={() => { setRecovery(!recovery); setCode(""); setError(""); }}
                className="font-bold text-muted-foreground hover:text-[#3B82F6]" data-testid="twofa-recovery-toggle">
          {recovery ? "Use the emailed code" : "Use a recovery code"}
        </button>
      </div>
    </form>
  );
}
